import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Download, Instagram, Link2, Mail, Linkedin, MessageCircle } from "lucide-react";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";

const fallbackImage = "https://images.unsplash.com/photo-1542992933-ce75d0187ec1?auto=format&fit=crop&w=1200&q=85";

export default function CampaignShareTools({ profile, profileSlug }) {
  const [style, setStyle] = useState(profile?.portfolio?.preview_style || "image");
  const [previewSource, setPreviewSource] = useState(profile?.portfolio?.preview_image || profile?.portfolio?.gallery?.[0] || fallbackImage);
  const [media, setMedia] = useState([]);
  const cardRef = useRef(null);
  const portfolio = profile?.portfolio || {};
  const image = previewSource || fallbackImage;
  const imageUrl = image.startsWith("http")
    ? image
    : `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/media/${image}`;
  const campaignUrl = (source) => `${process.env.REACT_APP_BACKEND_URL}/api/share/talent/${profileSlug}?source=${source}&campaign=portfolio-share`;

  useEffect(() => {
    setStyle(profile?.portfolio?.preview_style || "image");
    setPreviewSource(profile?.portfolio?.preview_image || profile?.portfolio?.gallery?.[0] || fallbackImage);
  }, [profile]);

  useEffect(() => {
    api.get("/portfolio/media").then(({ data }) => setMedia(data)).catch(() => setMedia([]));
  }, []);

  const selectStyle = async (nextStyle) => {
    const previousStyle = style;
    setStyle(nextStyle);
    try {
      await api.patch("/portfolio/preview-style", { style: nextStyle });
      toast.success("Default share preview updated.");
    } catch (error) {
      setStyle(previousStyle);
      toast.error(getErrorMessage(error));
    }
  };

  const selectPreviewImage = async (source) => {
    try {
      await api.patch("/portfolio/preview-image", { source });
      setPreviewSource(source);
      toast.success("Share image updated.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const copyCampaign = async (source) => {
    try {
      await navigator.clipboard.writeText(campaignUrl(source));
      toast.success(`${source[0].toUpperCase() + source.slice(1)} campaign link copied.`);
    } catch (error) {
      toast.error("Could not copy the link. Please copy it from the browser address bar.");
    }
  };

  const downloadCard = async () => {
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#0a0a0a", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `${profileSlug}-portfolio-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Share card downloaded.");
    } catch (error) {
      toast.error("Could not create the share card. Please try again.");
    }
  };

  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(campaignUrl("linkedin"))}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`View ${profile?.name}'s event portfolio: ${campaignUrl("whatsapp")}`)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(`${profile?.name}'s event portfolio`)}&body=${encodeURIComponent(campaignUrl("email"))}`;
  const usableGallery = (portfolio.gallery || []).filter((source) => {
    if (source.startsWith("http")) return true;
    const item = media.find((mediaItem) => mediaItem.id === source);
    return item && item.size >= 512;
  });

  return <section className="campaign-tools" data-testid="campaign-tools-panel">
    <div className="campaign-heading"><div><p className="eyebrow">PROMOTE YOUR WORK</p><h2>Make every share count.</h2></div><button data-testid="download-share-card-button" onClick={downloadCard}><Download size={15} /> Download card</button></div>
    <div className="campaign-grid">
      <div className="share-card-wrap"><div className={`share-card ${style}`} data-testid="social-preview-card" ref={cardRef}>{style === "image" && <img alt="Portfolio card background" crossOrigin="anonymous" src={imageUrl} />}<div className="share-card-content"><p>CREWED® / EVENT TALENT</p><h3>{profile?.name}</h3><strong>{portfolio.headline || "Event freelancer"}</strong><span>★ {portfolio.rating_average || "New"} rated talent</span></div></div><div className="card-style-toggle" data-testid="share-card-style-toggle"><button className={style === "image" ? "active" : ""} data-testid="share-card-image-style-button" onClick={() => selectStyle("image")}>Image-led</button><button className={style === "minimal" ? "active" : ""} data-testid="share-card-minimal-style-button" onClick={() => selectStyle("minimal")}>Minimal</button></div><p className="preview-picker-label">Share image</p><div className="preview-image-picker" data-testid="share-preview-image-picker">{usableGallery.map((source, index) => <button className={previewSource === source ? "active" : ""} data-testid={`share-preview-image-${index}`} key={source} onClick={() => selectPreviewImage(source)} type="button"><img alt={`Choose portfolio image ${index + 1}`} src={source.startsWith("http") ? source : `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/media/${source}`} />{previewSource === source && <span>Selected</span>}</button>)}</div></div>
      <div className="campaign-actions"><p>Each link adds a channel label to your profile insights.</p><button data-testid="copy-instagram-campaign-button" onClick={() => copyCampaign("instagram")}><Instagram size={16} /> Copy Instagram link</button><a data-testid="share-linkedin-campaign-link" href={linkedinUrl} rel="noreferrer" target="_blank"><Linkedin size={16} /> Share on LinkedIn</a><a data-testid="share-whatsapp-campaign-link" href={whatsappUrl} rel="noreferrer" target="_blank"><MessageCircle size={16} /> Share on WhatsApp</a><a data-testid="share-email-campaign-link" href={emailUrl}><Mail size={16} /> Send by email</a><button data-testid="copy-general-campaign-button" onClick={() => copyCampaign("share")}><Link2 size={16} /> Copy campaign link</button></div>
    </div>
  </section>;
}